import tableStyle from '@Styles/table.module.scss';
import { getYesNoIcon } from '@Utils/icon.utils.tsx';
import {
  Button,
  ErrorDisplay,
  Icons,
  LogoLoader,
  NotFound,
  ProfilePicture,
  QuickActionsDropdown,
  SectionCard,
  Table,
} from '@kg-internal/pv3-uikit';
import cx from 'classnames';
import get from 'lodash/get';
import { type JSX, ReactNode } from 'react';
import styles from './ListCard.module.scss';

type WithID<T> = Omit<T, 'id'> & {
  id: string;
  key?: string;
};

type Props<T> = {
  data: WithID<T>[];
  withDetails?: boolean;
  withActions?: {
    edit?: (id: string) => void;
    delete?: (id: string) => void;
    unassign?: (id: string) => void;
    view?: (id: string) => void;
  };
  detailsPath?: string;
  detailsSearchParams?: string;
  loading: boolean;
  error?: string;
  title: string;
  searchEntered?: boolean;
  structure: {
    header: string;
    key: string;
    boolean?: boolean;
    picture?: boolean;
    altKey?: string;
  }[];
  classname?: string;
};

type WrapperProps = {
  title: string;
  children: ReactNode;
};

export const CardWrapper = ({ title, children }: WrapperProps) => (
  <SectionCard className={styles.card}>
    <h2>{title}</h2>
    {children}
  </SectionCard>
);

const ListCard = <T,>({
  data,
  searchEntered = false,
  withDetails = false,
  withActions,
  detailsPath,
  detailsSearchParams,
  loading,
  error,
  title,
  structure,
  classname,
}: Props<T>) => {
  const mtable = 'mtable';
  const getQuickActions = (id: string) => {
    const actionOptions: {
      content: JSX.Element;
      label: string;
      danger?: boolean;
      onClick: () => void;
    }[] = [];

    if (withActions?.edit) {
      actionOptions.push({
        content: <>{Icons.Edit} Edit</>,
        label: 'Edit',
        onClick: () => withActions.edit && withActions.edit(id),
      });
    }

    if (withActions?.delete) {
      actionOptions.push({
        content: <>{Icons.Delete} Delete</>,
        danger: true,
        label: 'Delete',
        onClick: () => withActions.delete && withActions.delete(id),
      });
    }

    if (withActions?.view) {
      actionOptions.push({
        content: <>{Icons.Info} View</>,
        label: 'View',
        onClick: () => withActions.view && withActions.view(id),
      });
    }

    if (withActions?.unassign) {
      actionOptions.push({
        content: <>{Icons.ClosedCircle} Unassign</>,
        danger: true,
        label: 'Unassign',
        onClick: () => withActions.unassign && withActions.unassign(id),
      });
    }
    return actionOptions;
  };

  if (loading) {
    return (
      <CardWrapper title={title}>
        <div className={styles.loader}>
          <LogoLoader inline />
        </div>
      </CardWrapper>
    );
  }

  if (error) {
    return (
      <CardWrapper title={title}>
        <ErrorDisplay inline details={error} />
      </CardWrapper>
    );
  }

  if (data.length === 0) {
    return (
      <CardWrapper title={title}>
        <NotFound
          title={searchEntered ? 'No results found' : 'No data available'}
          details={searchEntered ? 'Try adjusting your search criteria.' : ''}
          hideButton
        />
      </CardWrapper>
    );
  }

  return (
    <CardWrapper title={title}>
      <Table
        className={cx(
          tableStyle.mtable,
          styles.format,
          classname,
          {
            [`${tableStyle[`${mtable}--withActions`]}`]: withActions,
          },
          {
            [`${tableStyle[`${mtable}--withDetails`]}`]: withDetails,
          },
        )}
        grid={false}>
        <thead>
          <tr>
            {structure.map((struct) => (
              <th key={struct.header}>{struct.header}</th>
            ))}
            {withDetails ? <th>More Details</th> : ''}
            {withActions ? <th>Actions</th> : ''}
          </tr>
        </thead>
        <tbody>
          {data.map((value: WithID<T>) => (
            <tr key={value.key ?? value.id}>
              {structure.map((struct) => {
                const key = struct.key as keyof T;

                if (struct.picture && struct.altKey) {
                  const name = get(value, struct.altKey);

                  return (
                    <td key={`${value.key ?? value.id}_picture`}>
                      <ProfilePicture name={name as string} url={get(value, key) as string} circular size='small' />
                    </td>
                  );
                }

                if (struct.boolean) {
                  return (
                    <td key={`${value.key ?? value.id}_${key.toString()}`} className={!get(value, key) ? 'empty' : ''}>
                      {getYesNoIcon(get(value, key) as boolean)}
                    </td>
                  );
                }

                const toDisplay = (get(value, key) as string) || '-';

                return (
                  <td key={`${value.key ?? value.id}_${key.toString()}`} className={!get(value, key) ? 'empty' : ''}>
                    {toDisplay}
                  </td>
                );
              })}
              {withDetails ? (
                <td key={`${value.key ?? value.id}_details`}>
                  {value.id ? (
                    <Button
                      type='link-ternary'
                      className={styles['list-card__view-more-btn']}
                      useReactRouterLink
                      href={`${detailsPath}/${value.id}${detailsSearchParams ?? ''}`}>
                      View More
                    </Button>
                  ) : (
                    'No details available'
                  )}
                </td>
              ) : (
                ''
              )}
              {withActions && (
                <td aria-label='Quick Actions' key={`${value.key ?? value.id}_actions`}>
                  <QuickActionsDropdown options={getQuickActions(value.id)} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </Table>
    </CardWrapper>
  );
};
export default ListCard;
